// User-facing copy per `?authError=` reason slug. Must cover every AuthErrorReason the
// callbacks emit (functions/api/auth/_lib/callback-helpers.ts) — the two lists can't share
// an import across the app/functions tsconfig boundary, so keep them in sync by hand.
const AUTH_ERROR_DESCRIPTIONS: Record<string, string> = {
  idp_error: 'The identity provider declined the sign-in request.',
  missing_code_state: "The identity provider's response was incomplete.",
  txn_missing: 'The sign-in attempt expired before it could complete.',
  txn_invalid: "The sign-in attempt couldn't be verified.",
  state_mismatch: 'Another sign-in attempt interrupted this one.',
  exchange_failed: 'Completing sign-in with the identity provider failed.',
};

export const UNKNOWN_AUTH_ERROR_REASON = 'unknown';

/** Collapses the raw (user-controllable) query value to a known slug — safe to display and to send to Sentry. */
export const normalizeAuthErrorReason = (raw: string | null): string => (raw && raw in AUTH_ERROR_DESCRIPTIONS ? raw : UNKNOWN_AUTH_ERROR_REASON);

export const describeAuthError = (raw: string | null): string => AUTH_ERROR_DESCRIPTIONS[normalizeAuthErrorReason(raw)] ?? 'The sign-in request could not be completed.';

// Mirrors ExchangeFailureDetail in functions/api/auth/_lib/callback-helpers.ts.
const EXCHANGE_FAILURE_DETAILS = new Set([
  'invalid_grant',
  'invalid_client',
  'access_denied',
  'invalid_request',
  'rate_limited',
  'idp_unavailable',
  'missing_tokens',
  'token_endpoint_error',
  'jwks_unavailable',
  'id_token_invalid',
  'network_error',
  'unknown',
]);

export const normalizeAuthErrorDetail = (raw: string | null): string | undefined => (raw && EXCHANGE_FAILURE_DETAILS.has(raw) ? raw : undefined);

const AUTH_ERROR_PARAMS = ['authError', 'authErrorDetail'] as const;

/** Copy of `params` without the one-shot callback error flags. */
export const withoutAuthErrorParams = (params: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(params);
  for (const key of AUTH_ERROR_PARAMS) next.delete(key);
  return next;
};

/** Makes /api/auth/login force a fresh IdP sign-in. Mirrors REAUTH_PARAM in functions/api/auth/_lib/return-to.ts. */
export const REAUTH_QUERY = 'reauth=1';
